import {
  CompletedProgramAssessmentsDataQuery,
  AssessmentAnswer,
  AssessmentQuestionType,
  MultipleChoiceScoredAssessmentAnswer,
  MultiSelectAssessmentAnswer,
  SignatureAssessmentAnswer,
  MultiSignatureAssessmentAnswer,
  InitialsAssessmentAnswer,
  FileUploadAssessmentAnswer,
  PatientContactAssessmentAnswer,
} from '../../generated/graphql';
import { getTimeStamp } from './time';
import { ProgramAssessmentQuestion } from './program-assessment';
import { isMultiSelectAssessmentQuestion } from './assessments';
import { getMultiSelectDisplayRows } from './answer-option-display';
import { createQuestionsForDisplay } from './assessment-responses';
import { getAnswerValue } from './answerHelpers';
import { formatPatientContactSnapshotAnswer } from './patientContactLabels';

type ProgramAssessment =
  CompletedProgramAssessmentsDataQuery['completedProgramAssessmentsByAssessment'][number];

export interface FormattedFormData {
  title: string;
  content: string;
}

const CHECKED_BOX = '\u2611';
const UNCHECKED_BOX = '\u2610';

function getCompletedByLabel(
  programAssessment: ProgramAssessment,
): string | null {
  const isPractitionerForm =
    programAssessment.assessment?.completableByProviderOnly === true;
  if (!isPractitionerForm) return null;
  const pa = programAssessment as ProgramAssessment & {
    completedByType?: string | null;
    completedByProviderUser?: { name?: string | null } | null;
    programAssessmentSignatureUsers?: Array<{
      signedAt?: unknown;
      providerUser?: { name?: string | null };
    }>;
  };
  const signatureProviderName = pa?.programAssessmentSignatureUsers?.find(
    (u) => u.signedAt,
  )?.providerUser?.name;
  const completedByProviderName =
    signatureProviderName ?? pa?.completedByProviderUser?.name;
  const completedByClient =
    isPractitionerForm && pa?.completedByType === 'patient';
  if (completedByProviderName) return completedByProviderName;
  if (completedByClient) return 'Client';
  return null;
}

function getMultiSelectOptionLabels(
  templateQuestion: ProgramAssessmentQuestion | null | undefined,
): string[] {
  if (templateQuestion && isMultiSelectAssessmentQuestion(templateQuestion)) {
    return (
      templateQuestion.answerOptions?.map((option) => option.answerOption) ?? []
    );
  }
  return [];
}

function formatMultiSelectNoteAnswers(
  selected: string[],
  optionLabels: string[],
): string {
  const rows = getMultiSelectDisplayRows(optionLabels, selected);
  if (!rows.length) {
    return 'Nothing selected';
  }

  return rows
    .map((row) => {
      if (row.kind === 'header') {
        return `**${row.label}**`;
      }
      return `${row.selected ? CHECKED_BOX : UNCHECKED_BOX} ${row.label}`;
    })
    .join('\n');
}

function formatAnswerBody(
  answer: AssessmentAnswer | undefined,
  templateQuestion: ProgramAssessmentQuestion | null | undefined,
): string | null {
  if (!answer) {
    return null;
  }

  switch (answer.questionType) {
    case AssessmentQuestionType.Statement:
      return null;
    case AssessmentQuestionType.MultiSelect: {
      const multiSelectAnswer = answer as MultiSelectAssessmentAnswer;
      return formatMultiSelectNoteAnswers(
        multiSelectAnswer.answers ?? [],
        getMultiSelectOptionLabels(templateQuestion),
      );
    }
    case AssessmentQuestionType.MultipleChoiceScored: {
      const scoredAnswer = answer as MultipleChoiceScoredAssessmentAnswer;
      const lines = [];
      if (scoredAnswer.subscale) {
        lines.push(`*${scoredAnswer.subscale}*`);
      }
      if (scoredAnswer.answer) {
        lines.push(scoredAnswer.answer);
      }
      return lines.join('\n') || null;
    }
    case AssessmentQuestionType.Signature: {
      const signatureAnswer = answer as SignatureAssessmentAnswer;
      if (!signatureAnswer.signatureName) {
        return 'Not signed';
      }
      const signedDate = signatureAnswer.signedAt
        ? new Date(signatureAnswer.signedAt).toLocaleString()
        : 'Unknown date';
      return `Signed by: ${signatureAnswer.signatureName} on ${signedDate}`;
    }
    case AssessmentQuestionType.MultiSignature: {
      const multiSignatureAnswer = answer as MultiSignatureAssessmentAnswer;
      if (!multiSignatureAnswer.signatures?.length) {
        return 'Not signed';
      }
      return multiSignatureAnswer.signatures
        .map((signature) => {
          const signedDate = new Date(signature.signedAt).toLocaleString();
          const role = signature.signatureRole || 'Signatory';
          return `- ${signature.signatureName} (${role}) - ${signedDate}`;
        })
        .join('\n');
    }
    case AssessmentQuestionType.Initials: {
      const initialsAnswer = answer as InitialsAssessmentAnswer;
      if (!initialsAnswer.signatureName) {
        return 'Not initialed';
      }
      const signedDate = initialsAnswer.signedAt
        ? new Date(initialsAnswer.signedAt).toLocaleString()
        : 'Unknown date';
      return `Initialed by: ${initialsAnswer.signatureName} on ${signedDate}`;
    }
    case AssessmentQuestionType.FileUpload: {
      const fileUploadAnswer = answer as FileUploadAssessmentAnswer;
      return fileUploadAnswer.fileName || 'No file uploaded';
    }
    case AssessmentQuestionType.PatientContact: {
      const patientContactAnswer = answer as PatientContactAssessmentAnswer;
      return formatPatientContactSnapshotAnswer(
        patientContactAnswer.contactSnapshot,
      );
    }
    default:
      return getAnswerValue(answer);
  }
}

/**
 * Formats a completed form's questions and answers into markdown for note creation.
 * Question copy is left as authored markdown so lists, links, bold, and underline
 * match the completed form instead of being wrapped in extra ** markers.
 */
export function formatFormDataForNote(
  programAssessment: ProgramAssessment,
  patientFirstName?: string,
): FormattedFormData {
  const assessment = programAssessment.assessment;
  const answers = (programAssessment.answers ?? []) as AssessmentAnswer[];
  const completedAt = programAssessment.completedAt;
  const additionalThoughts = programAssessment.additionalThoughts;

  const completionDate = completedAt
    ? getTimeStamp(completedAt, false)
    : 'Unknown date';
  const title = `${assessment.name} - ${completionDate}`;

  let content = '';

  if (patientFirstName) {
    content += `**Patient:** ${patientFirstName}\n`;
  }

  if (completedAt) {
    const formattedDate = new Date(completedAt).toLocaleString();
    content += `**Completed:** ${formattedDate}\n`;
  }

  const completedByLabel = getCompletedByLabel(programAssessment);
  if (completedByLabel) {
    content += `**Completed by:** ${completedByLabel}\n`;
  }

  if (content) {
    content += '\n';
  }

  const displayQuestions = createQuestionsForDisplay(
    assessment.questions as ProgramAssessmentQuestion[] | undefined,
    answers,
  );

  displayQuestions.forEach(
    ({ question: templateQuestion, questionText, matchedAnswer }) => {
      const prompt = (questionText || templateQuestion?.question || '').trim();
      const answerBody = formatAnswerBody(matchedAnswer, templateQuestion);

      if (!prompt && !answerBody) {
        return;
      }

      if (prompt) {
        content += `${prompt}\n\n`;
      }
      if (answerBody) {
        content += `${answerBody}\n\n`;
      }
    },
  );

  if (additionalThoughts) {
    content += `## Additional Notes from Patient\n\n"${additionalThoughts}"\n\n`;
  }

  return {
    title,
    content: content.trim(),
  };
}
